import { Box, Grid, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys } from "../../../../api/queryKeys";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import CustomCopyButton from "../../../../components/CustomCopyButton";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import { logos } from "../../environment/icons/iconExport";
import Guideline from "../components/Guideline";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import CursorConnectionForm from "./CursorConnectionForm";
import CURSOR_1 from "./images/CURSOR_1.png";
import CURSOR_2 from "./images/CURSOR_2.png";
import CURSOR_3 from "./images/CURSOR_3.png";

const CursorIntegration = () => {
  const [searchParams] = useSearchParams();
  const [guideMode, setGuideMode] = useState(false);
  const queryClient = useQueryClient();
  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();

  useEffect(() => {
    if (searchParams.has("viewonly")) {
      setGuideMode(true);
    }
  }, [searchParams]);

  const { isIntegrated, isIntegratedLoading } = useIsIntegrated("cursor");
  const [loading, setLoading] = useState(false);
  const { registerCursorIntegration } = useAPI();

  const onSubmit = (values) => {
    setLoading(true);
    registerCursorIntegration(values.admin_api_key.trim())
      .then(() => {
        queryClient.invalidateQueries({
          queryKey: integrationKeys.all,
        });
        Toast.success("Cursor integration successful");
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(`Something went wrong: ${error.response.data.detail}`);
        } else {
          Toast.error("An error occurred while processing the request.");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Box>
      <Guideline
        title={!isIntegrated ? "Connect Cursor using an Admin API Key." : ""}
        image={logos.CursorLogo}
        iconBorderRadius={0}
        name={"Cursor"}
        content={
          loading || isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <Typography variant={"h4"}>Cursor is integrated!</Typography>
          ) : (
            <div>
              {guideMode && <IntegrationGuideAlert />}
              <br></br>
              <Box mb="20px">
                <Grid container spacing={2} direction={"row"}>
                  <Grid item>
                    <SecondaryButton onClick={handleExpandAll}>
                      Expand All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton onClick={handleCollapseAll}>
                      Collapse All Screenshots
                    </SecondaryButton>
                  </Grid>
                </Grid>
              </Box>
              <GuidelineStepByStep>
                <GuidelineStep title="Navigate to the Cursor Admin console">
                  <Box
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                    }}
                  >
                    <Typography variant="body1">
                      Navigate to your Cursor Admin Settings to generate an
                      Admin API Key.
                    </Typography>
                    <PrimaryButton
                      onClick={() => {
                        window.open(
                          "https://cursor.com/dashboard?tab=settings",
                          "_blank"
                        );
                      }}
                      style={{ textTransform: "none", width: "fit-content" }}
                    >
                      Open Cursor Console Settings
                    </PrimaryButton>
                    <Typography variant="body1">
                      <strong>
                        Note: Admin API Key access is available only on Teams or
                        Enterprise plans.
                      </strong>
                    </Typography>
                    <ScreenshotAccordion
                      images={[
                        {
                          file: CURSOR_1,
                          altext: "Cursor Admin Settings",
                        },
                      ]}
                      onExpanded={addToExpandedList}
                    />
                  </Box>
                </GuidelineStep>
                <GuidelineStep title="Generate the Admin API Key">
                  <Box
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                    }}
                  >
                    <Typography variant="body1">
                      1. On the Settings page, scroll down to "Advanced"
                    </Typography>
                    <ScreenshotAccordion
                      images={[
                        {
                          file: CURSOR_2,
                          altext: "Cursor Admin API Key Creation location",
                        },
                      ]}
                      onExpanded={addToExpandedList}
                    />
                    <Typography variant="body1">
                      2. Give the key a name (e.g.,{" "}
                      <strong>Breeze Security Platform</strong>
                      <Box
                        component="span"
                        sx={{
                          display: "inline-flex",
                          verticalAlign: "middle",
                          ml: 0.5,
                        }}
                      >
                        <CustomCopyButton
                          copyTitle=""
                          copyText="Breeze Security Platform"
                        />
                      </Box>
                      )
                    </Typography>

                    <Typography variant="body1">3. Click save</Typography>

                    <ScreenshotAccordion
                      images={[
                        {
                          file: CURSOR_3,
                          altext: "Cursor Admin API Key Creation",
                        },
                      ]}
                      onExpanded={addToExpandedList}
                    />
                  </Box>
                </GuidelineStep>
                <GuidelineStep id="connectCursor" title="Connect Cursor">
                  <Box width={700}>
                    <CursorConnectionForm
                      onSubmit={onSubmit}
                      guideMode={guideMode}
                    />
                  </Box>
                </GuidelineStep>
              </GuidelineStepByStep>
            </div>
          )
        }
      />
    </Box>
  );
};

export default CursorIntegration;
